import {defineStore} from "pinia";
import axios from "@/axios";
import moment from 'moment-timezone';
import {computed} from "vue";
import {useToast} from "@/composables/useToast";
import i18n from '@/i18n';

export const useLanguageStore = defineStore({
    id : 'Language',
    state : ()=>({
        language : "ko",
    }),
    persist : {
        enabled : true,
        strategies : [
            {storage : localStorage, paths : ['language']}
        ]
    },
    actions : {
        set_language(newLang) {
            this.language = newLang;
            i18n.global.locale.value = newLang;
        }
    },
    getters : {
        get: () => i18n.global.t
    },
})
export const useLoginStore = defineStore({
    // e-bill과 연동하기 위한 이름설정
    id: "main",
    state : ()=>({
      loginInfo : null,
      jwtToken : null,
    }),
    persist : {
        enabled : true,
        strategies : [
            {storage : sessionStorage, paths: ['loginInfo', 'jwtToken']}
        ]
    },
    actions: {
        reset() {
            this.jwtToken = null;
            this.loginInfo = null;
        },
    },
    getters: {
        getJwtToken: state=>computed(()=>state.jwtToken ),
        getLoginInfo: state=>computed(()=>state.loginInfo),
        getLoginStatus: state=> computed(()=>state.jwtToken != null),
    },

})
export const useLoadingStore = defineStore({
    id: 'loading',
    state : ()=>({
        isLoading : false,
    }),
    actions: {
        set_isLoading(data){
            this.isLoading = data
        }
    },
    getters:{
        getIsLoading(){
            return this.isLoading;
        }
    },

})
export const useComStore = defineStore({
    id : 'common',
    state : ()=>({
        commonCode : [],
        groupCodes : [],
        useCommonCodes : [],
    }),
    persist : {
        enabled : true,
        strategies : [
            {storage : sessionStorage, paths: ['commonCode', 'groupCodes', 'useCommonCodes']}
        ]
    },
    actions : {
        setCommonCode() {
            return axios.get("/api/codes")
                .then(s=>{
                    this.$patch({commonCode : s.data})
                }).catch(e=> console.log(e))
        },
        setUseCommonCodes(){
            return axios.get("/api/useCodes")
                .then(s=>{
                    this.$patch({useCommonCodes : s.data})
                })
        },
        insertCommonCode(data) {
            axios.post("/api/code", data, {headers: { 'Content-Type': 'application/json' }})
                .then(s=>{
                    this.setCommonCode()
                })
        },
        deleteCommonCode(codeId){
            if(!confirm(i18n.global.t('deleteConfirm'))) return;
            const toast = useToast();
            axios.delete("/api/code/" + codeId)
                .then(s=>{
                    this.setCommonCode()
                    toast.success(i18n.global.t('deleteSuccess'));
                })
        },
        updateCommonCode(data){
            const toast = useToast();
            axios.put("/api/code", data, {headers: { 'Content-Type': 'application/json' }})
                .then(s=>{
                    this.setCommonCode();
                    toast.success(i18n.global.t('updateSuccess'));
                })
        },
        setGroupCodes(){
            return axios.get("/api/groupCodes")
                .then(s=>{
                    this.$patch({groupCodes : s.data})
                })
        }
    },

    getters : {
        getCodes : state => computed(() => state.commonCode),
        getUseCommonCodes : state=>computed(()=>state.useCommonCodes),
        getGroupCodes: state => computed(()=>state.groupCodes),
        today :  ()=> new Date(),
        formatTimeZoneDate: ()=> (dateTimeZone, format)=>{
            const date = moment(dateTimeZone);
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            date.tz(userTimeZone);

            return date.format(format);
        },
        getCommonNm : state => (codeId)=>{
            const result =state.useCommonCodes.find(s=>s.codeId == codeId)
            return result ? result.codeNm : null;
        },
        getCodeType : state => (grpCode) =>{
            const result = state.useCommonCodes.filter(s => s.grpCode == grpCode).sort((a,b)=> a.ord - b.ord)
            return result ? result : null;
        },
        formatVideoSize: () =>(videoSize)=> {
            let videoSizeKb = videoSize / 1024.0;
            if(videoSizeKb < 1024){
                return Math.round(videoSizeKb) + "KB";
            }else{
                let videoSizeMb  = videoSizeKb / 1024.0 ;
                if(videoSizeMb < 1024.0){
                    return Math.round(videoSizeMb) + "MB";
                } else {
                    let videoSizeGb = videoSizeMb / 1024.0;
                    return Math.round(videoSizeGb) + "GB";
                }
            }
        },
        formatVideoTime : () => (videoTime)=>{
            if(videoTime > 3600){
                let hours = Math.floor(videoTime / 3600);
                let minutes = Math.floor((videoTime % 3600) / 60);
                let seconds = Math.round(videoTime % 60);
                let formattedTime = `${hours}h ${minutes}m ${seconds}s`;
                return formattedTime;
            } else if(videoTime > 60){
                let minutes = Math.floor(videoTime / 60);
                let seconds = Math.round(videoTime % 60);
                let formattedTime = `${minutes}m ${seconds}s`;
                return formattedTime;
            }else{
                return `${Math.round(videoTime)}s`;
            }
        },
    },
})
export const useFolderStore = defineStore({
    id: "Folder",
    state : ()=>({
        folder : null,
        treeData : [],
    }),
    persist : {
        enabled : true,
        strategies : [
            {storage : sessionStorage, paths: ['folder']}
        ]
    },
    actions :{
        getFolder(){
            return axios.get("/folder")
                .then(s=>{
                    this.$patch({folder : s.data})
                })
                .catch()
        },
        saveFolder(data){
            axios.post("/folder", data, {
                headers: { 'Content-Type': 'application/json' }
            }).then(s=>{
                this.getFolder()
            })
        },
        deleteFolder(id){
            const toast = useToast();
            axios.delete("/folder/"+id)
                .then(s=>{
                    toast.success(i18n.global.t('deleteSuccess'));
                    this.getFolder()
                })
        },
        updateFolder(data){
            axios.put("/folder", data,  {
                headers: { 'Content-Type': 'application/json' }
            }).then(s=>{
                this.getFolder()
            })
        }
    },
    getters : {
        getMinFolder : state => ()=>{
            if(!state.folder) return null;
            let upperFolders = state.folder.filter(f => f.upperFolderId == null)
            let folderId = upperFolders.map(f=> f.folderId)
            return Math.min(...folderId);
        },
        getOneFolder :state => (currentFolder)=>{
            if(!state.folder || currentFolder == null) return null;
            return state.folder.find(f=> {
                return f.folderId == currentFolder
            }) || null;
        },
        getChildrenFolder : state => (upperFolderId)=>{
            if(!state.folder) return [];
            return state.folder.filter(f=>{
                if(f.upperFolderId != null) return upperFolderId == f.upperFolderId
            })
        },
        getTree :state => ()=>{
            return new Promise((resolve, reject) => {
                let data = null;
                if(state.folder != null){
                    data = state.folder.map(item => ({
                        id: `folder-${item.folderId}`,
                        parent: item.upperFolderId != null ? `folder-${item.upperFolderId}` : '#',
                        text: item.folderName,
                    }))
                    resolve(data);
                } else {
                    setTimeout(()=>{
                        data = state.folder.map(item => ({
                            id: `folder-${item.folderId}`,
                            parent: item.upperFolderId != null ? `folder-${item.upperFolderId}` : '#',
                            text: item.folderName,
                        }))
                        resolve(data);
                    }, 1000)
                }

            });
        },
        transformData :state => (responseData)=>{
            return responseData.map();
        },
        computedId : state => (stringId) =>{
            if(stringId != null ){
                return stringId.split("-")[1]
            }else{
                return "" ;
            }

        },
        getNavFolder : state => (currentFolder) =>{
            if(!state.folder) return [];
            let upperFolders =  [];
            for(let upperFolder ; ; ){
                upperFolder = upperFolders.length ===0
                    ? upperFolder = state.folder.find(f=> {return currentFolder == f.folderId})
                    : upperFolder = state.folder.find(f=> {return f.folderId == upperFolders[0]?.upperFolderId })
                if(upperFolder == null) {
                    return upperFolders;
                }else{
                    upperFolders.unshift(upperFolder);
                }
            }
        },
        isFolder : state => (currentFolder) =>{
            if(!state.folder) return false;
            return state.folder.some(f => {
                return f.folderId == currentFolder
            });
        },
    }
})
export const useAgeStore = defineStore({
    id:"Age",
    state : ()=>({
        ages : null,
        countAges : 0
    }),
    persist : {
        enabled : true,
        strategies : [
            {storage : sessionStorage, paths: ['ages']}
        ]
    },
    actions :{
        setAges(){
            return axios.get("/api/ages")
                .then(s=>{
                    this.$patch({ages : s.data.dataList, countAges : s.data.total})
                })
        },
        updateAge(data){
            axios.put("/api/age", data, {headers: { 'Content-Type': 'application/json' }})
                .then(()=>{
                    this.setAges();
                })
        },
        deleteAge(ageId){
            if(confirm(i18n.global.t('deleteConfirm'))){
                const toast = useToast();
                axios.delete("/api/age/" + ageId , {headers: { 'Content-Type': 'application/json' }})
                    .then(()=>{
                        toast.success(i18n.global.t('deleteSuccess'));
                        this.setAges();
                    })
            }
        },
        insertAge(data){
            axios.post("/api/ages", data, {headers: { 'Content-Type': 'application/json' }})
                .then(()=>{
                    this.setAges();
                })
        }
    },
    getters :{
        getAges : state => computed(()=>state.ages),
        getRecentAge: state => (state.ages && state.ages.length > 0 ? state.ages[0].ageId : null),
        getCountAges : state => computed(()=>state.countAges)
    }
})